import React, { useState } from "react";
import { Shield, ChevronDown, ChevronUp, Info } from "lucide-react";

const CATEGORY_META = {
  borrower_strength: { label: "Borrower Strength", color: "bg-blue-500", lightBg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  deal_quality: { label: "Deal Quality", color: "bg-emerald-500", lightBg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  market_strength: { label: "Market Strength", color: "bg-amber-500", lightBg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  execution_risk: { label: "Execution Risk", color: "bg-purple-500", lightBg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" },
};

export default function RiskBreakdown({ riskScore, onRecompute, isAdmin }) {
  const [expanded, setExpanded] = useState(false);

  if (!riskScore) return null;

  const explanations = riskScore.score_explanations || {};
  const hasExplanations = Object.values(explanations).some(arr => arr && arr.length > 0);

  return (
    <div data-testid="risk-breakdown">
      {/* Existing score display is handled by Deal360Page — this adds the explanations */}
      {hasExplanations && (
        <div className="mt-4 pt-4 border-t border-[#f0ece4]">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center justify-between w-full group"
            data-testid="risk-breakdown-toggle"
          >
            <div className="flex items-center gap-1.5">
              <Info className="h-3.5 w-3.5 text-[#999]" />
              <span className="text-[10px] uppercase tracking-[0.12em] text-[#888] font-bold group-hover:text-emerald-700 transition-colors">Score Breakdown</span>
            </div>
            {expanded ? <ChevronUp className="h-3.5 w-3.5 text-[#bbb]" /> : <ChevronDown className="h-3.5 w-3.5 text-[#bbb]" />}
          </button>
          {expanded && (
            <div className="mt-3 space-y-3" data-testid="risk-explanations">
              {Object.entries(explanations).map(([category, reasons]) => {
                const meta = CATEGORY_META[category] || { label: category, color: "bg-gray-500", lightBg: "bg-gray-50", text: "text-gray-700", border: "border-gray-200" };
                const score = category === "borrower_strength" ? riskScore.borrower_strength_score
                  : category === "deal_quality" ? riskScore.deal_quality_score
                  : category === "market_strength" ? riskScore.market_strength_score
                  : category === "execution_risk" ? riskScore.execution_risk_score
                  : null;

                if (!reasons || reasons.length === 0) return null;

                return (
                  <div key={category} className={`rounded-lg ${meta.lightBg} border ${meta.border} p-3`} data-testid={`risk-category-${category}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-[10px] font-bold ${meta.text} uppercase tracking-wider`}>{meta.label}</span>
                      {score !== null && <span className={`text-xs font-bold ${meta.text}`}>{score}/100</span>}
                    </div>
                    <div className="space-y-1">
                      {reasons.map((reason, i) => (
                        <div key={i} className="flex items-start gap-1.5">
                          <div className={`h-1 w-1 rounded-full ${meta.color} mt-1.5 shrink-0`} />
                          <span className="text-[10px] text-[#555] leading-relaxed">{reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Recompute button for admins */}
      {isAdmin && onRecompute && (
        <div className="mt-3 pt-3 border-t border-[#f0ece4]">
          <button
            onClick={onRecompute}
            className="w-full text-center text-[10px] font-bold text-emerald-700 hover:text-emerald-600 py-1.5 rounded-lg hover:bg-emerald-50 transition-colors"
            data-testid="recompute-risk-btn"
          >
            Recompute Risk Score
          </button>
        </div>
      )}
    </div>
  );
}
